import React from 'react';
import './ConversationCard.css';
import ImageGallery from './ImageGallery';

const ConversationCard = ({ conversation, onSelect, isSelected, userInfo }) => {
  const {
    listing_address,
    listing_info = {},
    latest_message = {},
    other_participants = [],
    unread_count = 0,
  } = conversation;

  const getPropertyImages = () => {
    let images = [];
    
    // Handle photos array
    if (Array.isArray(listing_info.photos) && listing_info.photos.length > 0) {
      images = listing_info.photos.map(photo => photo.url || photo).filter(Boolean);
    }
    
    // Handle media_urls
    const media = listing_info.media_urls || conversation.media_urls;
    if (media && images.length === 0) {
      try {
        const parsed = typeof media === 'string' ? JSON.parse(media) : media;
        if (Array.isArray(parsed)) {
          images = parsed.map(item => 
            typeof item === 'object' ? (item.url || item.src || item) : item
          ).filter(Boolean);
        } else if (parsed && typeof parsed === 'object') {
          // Handle object with primary and others
          if (parsed.primary) images.push(parsed.primary);
          if (Array.isArray(parsed.others)) {
            images = images.concat(parsed.others);
          }
        }
      } catch (_) {
        if (typeof media === 'string' && media.startsWith('http')) {
          images = [media];
        }
      }
    }
    
    // Fallback to individual image properties
    if (images.length === 0) {
      const candidates = [
        listing_info.primary_photo_url,
        listing_info.photo,
        listing_info.thumbnail,
        conversation.listing_photo_url,
        conversation.listing_photo,
        conversation.photo_url,
      ].filter(Boolean);
      images = candidates;
    }
    
    // Filter out invalid URLs and remove duplicates
    return [...new Set(images.filter(img => img && typeof img === 'string' && img.startsWith('http')))];
  };

  const getNumber = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        const n = Number(v);
        return Number.isNaN(n) ? null : n;
      }
    }
    return null;
  };

  const bedrooms = getNumber(listing_info, [
    'bedrooms', 'number_bedrooms', 'beds', 'bedrooms_total', 'bedrooms_count'
  ]);
  const bathrooms = getNumber(listing_info, [
    'bathrooms', 'bathroom_total', 'bathrooms_total', 'washrooms', 'washroom_total'
  ]);
  const parking = getNumber(listing_info, [
    'parking_spaces', 'parking', 'parking_count', 'parkingTotal', 'num_parking'
  ]);

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const t = new Date(timestamp);
    const diff = (now - t) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const propertyImages = getPropertyImages();
  const participant = other_participants[0] || {};

  return (
    <div
      className={`conversation-card ${isSelected ? 'selected' : ''} ${unread_count > 0 ? 'unread' : ''}`}
      onClick={() => onSelect(conversation)}
    >
      <div className="conversation-image">
        <ImageGallery images={propertyImages} alt="Property" />
        {unread_count > 0 && <div className="unread-indicator">{unread_count}</div>}
      </div>

      <div className="conversation-content">
        <div className="price-header">
          <div className="property-price">
            ${Number(listing_info.price || 0).toLocaleString()}
            <span className="price-period">/month</span>
          </div>
          <div className="conversation-time">{formatTimeAgo(latest_message.time)}</div>
        </div>

        <div className="property-address">{listing_address || 'Property Address'}</div>
        <div className="property-location">
          {listing_info.neighborhood && listing_info.city 
            ? `${listing_info.neighborhood}, ${listing_info.city}, Ontario`
            : listing_info.city 
            ? `${listing_info.city}, Ontario`
            : ''}
        </div>

        <div className="property-specs">
          {bedrooms !== null && (
            <span className="spec-item">🛏 {bedrooms} bed{bedrooms === 1 ? '' : 's'}</span>
          )}
          {bathrooms !== null && (
            <span className="spec-item">🚿 {bathrooms}</span>
          )}
          {parking !== null && (
            <span className="spec-item">🅿️ {parking}</span>
          )}
          {listing_info.property_type && (
            <span className="spec-item">{listing_info.property_type}</span>
          )}
        </div>

        <div className="latest-message-section">
          <div className="participant-info">
            <span>{participant.role === 'agent' ? '👔' : '👤'}</span>
            <span className="participant-name">{participant.name || 'Participant'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationCard;
